import { useEffect, useRef } from "react"
import { AppState } from "react-native"
import { Auth } from "aws-amplify"
import { crashlyticsWrap } from "src/tags/crashlytics"
import { useSetRecoilState } from "recoil"
import { tokensState } from "src/recoil/atoms/authorization/tokensState"

/**
 * Cognitoトークンの自動リフレッシュフック
 *
 * アプリがバックグラウンドからフォアグラウンドに復帰した際に、
 * Cognitoセッションを確認し、必要に応じてトークンを自動リフレッシュします。
 *
 * これにより、1時間のアクセストークン期限切れによる500エラーを防ぎます。
 *
 * @see https://github.com/eole-repos/ra9ra9_backend/issues/631
 */
export const useCognitoTokenRefresh = () => {
  const appState = useRef(AppState.currentState)
  const lastRefreshTime = useRef<number>(Date.now())
  const setTokens = useSetRecoilState(tokensState)

  useEffect(() => {
    const subscription = AppState.addEventListener("change", async (nextAppState) => {
      // バックグラウンド → フォアグラウンドの遷移を検知
      if (appState.current.match(/inactive|background/) && nextAppState === "active") {
        const now = Date.now()
        const timeSinceLastRefresh = now - lastRefreshTime.current

        // 最後のリフレッシュから5分以上経過している場合のみ実行
        // (頻繁なバックグラウンド/フォアグラウンド切り替えへの対策)
        if (timeSinceLastRefresh > 5 * 60 * 1000) {
          try {
            crashlyticsWrap.log("Attempting to refresh Cognito session on foreground")

            // Auth.currentSession() は内部で自動的にトークンをリフレッシュする
            // アクセストークンが期限切れの場合、リフレッシュトークンを使用して新しいトークンを取得
            const session = await Auth.currentSession()

            const accessToken = session.getAccessToken()
            const expiresIn = accessToken.getExpiration() - Math.floor(Date.now() / 1000)

            // リフレッシュされたトークンをRecoil stateに反映
            // これにより、customAPI.tsがリフレッシュされたトークンを使用できるようになる
            setTokens({
              accessToken: accessToken.getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken(),
              expiresIn: expiresIn.toString(),
              tokenType: "Bearer",
            })

            crashlyticsWrap.log(
              `Cognito session refreshed and synced to state. Token expires in ${Math.floor(expiresIn / 60)} minutes`
            )

            lastRefreshTime.current = now
          } catch (error) {
            // エラーの種類に応じて処理を分岐
            if (error instanceof Error) {
              if (error.message === "No current user") {
                // ユーザーがログアウトしている場合は正常
                crashlyticsWrap.log("No current user - user is not logged in")
              } else if (error.message.includes("Refresh Token has expired")) {
                // リフレッシュトークンも期限切れの場合
                // この場合、ユーザーは再ログインが必要
                crashlyticsWrap.recordError(
                  new Error("Refresh token expired - user needs to re-login"),
                  "useCognitoTokenRefresh"
                )
                // ログアウト処理は既存のエラーハンドリング機構に任せる
              } else {
                // その他の予期しないエラー
                crashlyticsWrap.recordError(error, "useCognitoTokenRefresh - unexpected error")
              }
            } else {
              crashlyticsWrap.recordError(
                new Error(`Unknown error during token refresh: ${String(error)}`),
                "useCognitoTokenRefresh"
              )
            }
          }
        }
      }

      appState.current = nextAppState
    })

    return () => {
      subscription.remove()
    }
  }, [setTokens])
}
